import { phaseNames, stateNames } from "./constants";
import faker from "faker";
import * as CSSO from "../../../shared/types/csso.types";
import { createContactInfo, createId } from "./shared";
import MockNotifications from "./notifications";

/**
 * createPhases
 * @return {*}  {CSSO.Phase[]}
 */
const createPhases = (): {
  phases: CSSO.Phase[];
  currentPhase: number;
} => {
  const phases: CSSO.Phase[] = Array(phaseNames.length);

  const currentPhase = faker.datatype.number({
    min: 0,
    max: phases.length - 1,
  });

  for (let i = 0; i <= currentPhase; ++i) {
    phases[i] = {
      id: "",
      name: phaseNames[i],
      status: {
        id: "",
        name: faker.random.arrayElement(stateNames),
      },
      index: i,
      completionDate: faker.date.recent().toString(),
    };
  }

  for (let i = currentPhase + 1; i < phases.length; ++i) {
    phases[i] = {
      id: "",
      name: phaseNames[i],
      status: null,
      index: i,
      completionDate: null,
    };
  }

  return { phases, currentPhase };
};

/**
 * createPackageTiming
 * @return {*}  {PackageDetailsTiming}
 */
const createPackageTiming = (): CSSO.PackageTiming => {
  return {
    projectedEndDate: faker.date.future().toString(),
    requestedByDate: faker.date.future().toString(),
    siteReadyDate: faker.date.future().toString(),
    daysOffSchedule: Math.trunc(Math.random() * 10) - 5,
    nextCeContact: {
      quantity: faker.datatype.number({ min: 0, max: 100 }),
      units: faker.random.arrayElement(["days"]),
    },
  };
};

/**
 * createPackageContact
 * @return {*}  {CSSO.PackageContactInfo}
 */
const createPackageContact = (): CSSO.PackageContactInfo => {
  return {
    mainContact: `${faker.name.firstName()} ${faker.name.lastName()}`,
    coordinator: createContactInfo(),
  };
};

/**
 * createPackage
 * @return {*}  {CSSO.Package}
 */
const createPackage = (
  projectId?: string,
  packageId?: string,
  notificationsCount?: number
): CSSO.Package => {
  const { phases, currentPhase } = createPhases();

  const notifCount = notificationsCount ?? Math.trunc(Math.random() * 15 + 1);
  projectId = projectId ?? createId();
  packageId = packageId ?? createId();

  const notifications = MockNotifications.getMockNotifications(notifCount);

  // Intentionally slightly biased towards shorter names
  let name =
    faker.datatype.number(10) < 5
      ? `${faker.company.catchPhraseAdjective()} ${faker.hacker.ingverb()}`
      : `${faker.hacker.ingverb()}`;

  // Ensure first letter is capitalized
  if (name.charAt(0).toUpperCase() !== name.charAt(0)) {
    name = name.charAt(0).toUpperCase() + name.substr(1);
  }

  const response: CSSO.Package = {
    projectId: /* istanbul ignore next */ projectId ?? createId(),
    id: /* istanbul ignore next */ packageId ?? createId(),
    name: name,
    phases: phases,
    timing: createPackageTiming(),
    contacts: createPackageContact(),
    currentPhase: currentPhase,
    notifications: notifications,
  };

  return response;
};

/**
 * getMockPackages
 * @param {number} numberOfPackages
 * @param {string} projectId
 * @return {*}  {Array<CSSO.Package>}
 */
const getMockPackages = (
  numberOfPackages: number,
  projectId?: string
): Array<CSSO.Package> => {
  const packages: Array<CSSO.Package> = [];
  while (packages.length < numberOfPackages) {
    const aPackage = createPackage(projectId);
    let skip = false;
    for (const existingPackage of packages) {
      if (
        existingPackage.name === aPackage.name ||
        existingPackage.id === aPackage.id
      ) {
        skip = true;
        break;
      }
    }
    if (!skip) {
      packages.push(aPackage);
    }
  }
  return packages;
};

/* istanbul ignore next */
const getMockPackage = (
  projectId?: string,
  packageId?: string
): CSSO.Package => {
  return createPackage(projectId, packageId);
};

export default {
  createPackage,
  getMockPackages,
  getMockPackage,
  createPhases,
  createPackageContact,
  createPackageTiming,
};
